import { useUserStoreWithOut } from "@/store/modules/user";
import { message, Modal } from "ant-design-vue";
import axios, {
  AxiosInstance,
  AxiosError,
  AxiosResponse,
  Method,
  AxiosRequestConfig,
  ResponseType,
} from "axios";

export type ErrorMessageMode = "none" | "modal" | "message" | undefined;

export interface ReqParams {
  url?: string;
  data?: any;
  query?: any;
  headers?: AxiosRequestConfig<any>["headers"];
  params?: any;
  timeout?: number;
  responseType?: ResponseType;
  errorMessageMode?: ErrorMessageMode;
}

const apiProxyPrefix = import.meta.env.VITE_GLOB_API_PROXY_PREFIX;
const apiUrl = import.meta.env.VITE_GLOB_API_URL;
export const baseUrl = import.meta.env.PROD ? apiUrl : apiProxyPrefix;

class RequestHttp {
  public readonly service: AxiosInstance;
  public constructor() {
    this.service = axios.create({
      validateStatus: (status) => status >= 200 && status < 300,
      baseURL: baseUrl,
      timeout: 10 * 10000,
      withCredentials: true,
    });

    this.initInterceptors();
  }

  private initInterceptors() {
    this.service.interceptors.request.use((config) => {
      const userStore = useUserStoreWithOut();
      const token = userStore.getToken;
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    });

    this.service.interceptors.response.use(
      (res: AxiosResponse) => {
        if (res.headers["refresh-token"]) {
          const userStore = useUserStoreWithOut();
          userStore.setToken(res.headers["refresh-token"]);
        }
        return res;
      },
      async (error: AxiosError) => {
        const { response: res } = error;
        if (res) {
          if (res.status == 401) {
            await useUserStoreWithOut().logout(true);
          } else {
            const title = res.headers["error-message-title"]
              ? decodeURIComponent(res.headers["error-message-title"])
              : undefined;
            const mode =
              (res.headers["error-message"] as ErrorMessageMode) ?? "message";
            const msg = this.getErrorMessage(res);
            await this.showError(msg, mode, title);
          }
        } else {
          await this.showError(
            (error as Error)?.message ?? "网络连接错误，请检查网络后重试",
          );
        }
        return Promise.reject(error);
      },
    );
  }

  // 防止短时间内多次请求都报错
  private lastShowMessageTime = 0;
  private lastShowMessage = "";
  private async showError(
    msg: string,
    errorMessageMode?: ErrorMessageMode,
    title?: string,
  ) {
    const now = new Date().valueOf();
    if (msg == this.lastShowMessage && now < this.lastShowMessageTime + 1000) {
      return;
    }
    this.lastShowMessageTime = now;
    this.lastShowMessage = msg;

    errorMessageMode = errorMessageMode ?? "message";

    if (errorMessageMode === "modal") {
      Modal.error({ title: title ?? "错误提示", content: msg });
    } else if (errorMessageMode === "message") {
      message.error(msg);
    }
  }

  private getErrorMessage(res: AxiosResponse, defMsg?: string) {
    let msg = "";
    if (typeof res.data == "string") {
      msg = res.data;
    } else if (typeof res.data?.message == "string") {
      msg = res.data?.message;
    } else if (Array.isArray(res.data?.message)) {
      msg = String(res.data.message[0]);
    }
    msg ??= "";
    if (msg.startsWith("<!")) {
      msg = "请求失败";
    }
    return msg || defMsg || `${res.status} ${res.statusText}`;
  }

  private async request<T = any>(
    method: Method,
    params?: ReqParams,
  ): Promise<T | undefined>;
  private async request<T = any>(
    method: Method,
    params: ReqParams & { default: T },
  ): Promise<T>;
  private async request<T = any>(
    method: Method,
    params: ReqParams & { root: true },
  ): Promise<AxiosResponse<T> | AxiosError<T>>;
  private async request<T = any>(
    method: Method,
    params?: ReqParams & { default?: T; root?: true },
  ): Promise<T | AxiosResponse<T> | AxiosError<T> | undefined> {
    let url = params?.url ?? "";
    if (url.startsWith(baseUrl)) {
      url = url.substring(baseUrl.length, url.length);
    }
    if (params?.params) {
      Object.keys(params.params).forEach((key) => {
        url = url.replace(`:${key}`, encodeURIComponent(params.params[key]));
      });
    }

    const cfg: AxiosRequestConfig<T> = {
      method: method,
      url: url,
      headers: params?.headers ?? {},
      params: params?.query,
      data: params?.data,
      timeout: params?.timeout,
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
      responseType: params?.responseType,
    };

    try {
      const res = await this.service.request<T>(cfg);
      if (params?.root) {
        return res;
      } else {
        if (typeof res.data === "undefined" || (res.data as any) === "") {
          return {} as T;
        } else {
          return res.data;
        }
      }
    } catch (err) {
      const error = err as AxiosError<T>;
      if (params?.root) {
        return error;
      } else {
        if (typeof params?.default != "undefined") {
          return params.default;
        } else {
          return undefined;
        }
      }
    }
  }

  // 常用方法封装
  public get<T = any>(ps?: ReqParams): Promise<T | undefined>;
  public get<T = any>(ps: ReqParams & { default: T }): Promise<T>;
  public get<T = any>(
    ps: ReqParams & { root: true },
  ): Promise<AxiosResponse<T> | AxiosError<T>>;
  public get<T = any>(ps?: ReqParams & { default?: T; root?: true }) {
    return this.request<T>("get", ps);
  }

  public post<T = any>(ps?: ReqParams): Promise<T | undefined>;
  public post<T = any>(ps: ReqParams & { default: T }): Promise<T>;
  public post<T = any>(
    ps: ReqParams & { root: true },
  ): Promise<AxiosResponse<T> | AxiosError<T>>;
  public post<T = any>(ps?: ReqParams & { default?: T; root?: true }) {
    return this.request<T>("post", ps);
  }

  public put<T = any>(ps?: ReqParams): Promise<T | undefined>;
  public put<T = any>(ps: ReqParams & { default: T }): Promise<T>;
  public put<T = any>(
    ps: ReqParams & { root: true },
  ): Promise<AxiosResponse<T> | AxiosError<T>>;
  public put<T = any>(ps?: ReqParams & { default?: T; root?: true }) {
    return this.request<T>("put", ps);
  }

  public delete<T = any>(ps?: ReqParams): Promise<T | undefined>;
  public delete<T = any>(ps: ReqParams & { default: T }): Promise<T>;
  public delete<T = any>(
    ps: ReqParams & { root: true },
  ): Promise<AxiosResponse<T> | AxiosError<T>>;
  public delete<T = any>(ps?: ReqParams & { default?: T; root?: true }) {
    return this.request<T>("delete", ps);
  }

  public patch<T = any>(ps?: ReqParams): Promise<T | undefined>;
  public patch<T = any>(ps: ReqParams & { default: T }): Promise<T>;
  public patch<T = any>(
    ps: ReqParams & { root: true },
  ): Promise<AxiosResponse<T> | AxiosError<T>>;
  public patch<T = any>(ps?: ReqParams & { default?: T; root?: true }) {
    return this.request<T>("patch", ps);
  }
}

// export default new RequestHttp(config);

const instance = new RequestHttp();
export default instance;
